/**
 * Reusable Swiper initializer
 * -----------------------------------------------------------------------------
 * Auto-initializes every element carrying a `data-swiper-instance` attribute as
 * a Swiper (v8.1.6) slider, reading its configuration from `data-` attributes.
 *
 * Performance:
 *   - Swiper itself is loaded lazily via the shared `whenSwiperReady()` helper
 *     defined in snippets/_head_scripts.liquid (on-demand on the product
 *     template, eager elsewhere). This file only constructs Swiper *after* the
 *     library is available.
 *   - By default each slider is constructed lazily via IntersectionObserver,
 *     just before it scrolls into view, so off-screen carousels never pay the
 *     init cost up front. Set `data-swiper-init-on-visible="false"` on
 *     above-the-fold sliders to construct immediately.
 *   - Set `data-swiper-init-after="load"` to hold construction until the
 *     window `load` event, keeping the init cost (and any layout it causes)
 *     out of the LCP window for sliders near the top of the page.
 *
 * Authoring (Liquid):
 *   Add the marker + the `.swiper` / `.swiper-wrapper` / `.swiper-slide`
 *   structure, then configure with attributes:
 *
 *     <div class="swiper" data-swiper-instance
 *          data-swiper-slides-per-view="1"
 *          data-swiper-space-between="16"
 *          data-swiper-speed="500"
 *          data-swiper-loop
 *          data-swiper-autoplay="4000"
 *          data-swiper-breakpoints='{"768":{"slidesPerView":2},"1024":{"slidesPerView":3}}'>
 *       <div class="swiper-wrapper">
 *         <div class="swiper-slide">…</div>
 *       </div>
 *       <div class="swiper-pagination"></div>
 *       <button class="swiper-button-prev"></button>
 *       <button class="swiper-button-next"></button>
 *     </div>
 *
 *   - Any Swiper option can be set with `data-swiper-<kebab-option>`; the value
 *     is coerced (true/false → boolean, numeric → number, {…}/[…] → JSON,
 *     bare attribute → true). e.g. `data-swiper-centered-slides`,
 *     `data-swiper-slides-per-view="auto"`.
 *   - For complex configs use a single `data-swiper-config='{…}'` JSON blob.
 *     Individual attributes override matching keys from the blob.
 *   - Pagination / navigation / scrollbar are auto-wired if the corresponding
 *     `.swiper-pagination` / `.swiper-button-next`+`.swiper-button-prev` /
 *     `.swiper-scrollbar` elements exist inside the container. Pass
 *     `data-swiper-pagination="false"` (etc.) to opt out, or a JSON object to
 *     customize.
 *   - Controls outside the container can be wired with selectors, e.g.
 *     `data-swiper-navigation='{"prevEl":"#id-prev","nextEl":"#id-next"}'`.
 *   - Each element dispatches a bubbling `swiper:init` event (detail.swiper)
 *     once constructed, for attaching `on` handlers from section scripts.
 * -----------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var SELECTOR = '[data-swiper-instance]';

  // Attributes consumed by this module rather than passed to Swiper.
  var RESERVED = {
    swiperInstance: true,
    swiperConfig: true,
    swiperBreakpoints: true,
    swiperInitOnVisible: true,
    swiperLazyMargin: true,
    swiperInitAfter: true,
  };

  // Sensible, override-able defaults applied to every instance.
  var DEFAULTS = {
    slidesPerView: 1,
    spaceBetween: 0,
    speed: 500,
    grabCursor: true,
    watchOverflow: true, // don't enable dragging/pagination when slides fit
    a11y: { enabled: true },
  };

  /**
   * Loads (if needed) then runs `cb` once Swiper is on the page. Falls back to
   * a short poll if the shared helper isn't present for some reason.
   */
  function whenReady(cb) {
    if (typeof window.whenSwiperReady === 'function') {
      window.whenSwiperReady(cb);
      return;
    }
    if (typeof window.Swiper !== 'undefined') {
      cb();
      return;
    }
    var tries = 0;
    var timer = setInterval(function () {
      if (typeof window.Swiper !== 'undefined') {
        clearInterval(timer);
        cb();
      } else if (++tries > 100) {
        // ~10s; give up quietly rather than spin forever.
        clearInterval(timer);
      }
    }, 100);
  }

  /** Coerce a raw attribute string into a boolean / number / JSON / string. */
  function coerce(raw) {
    if (raw === '' || raw == null) return true; // bare attribute => enabled
    var value = raw.trim();
    if (value === 'true') return true;
    if (value === 'false') return false;
    var first = value.charAt(0);
    if (first === '{' || first === '[') {
      try {
        return JSON.parse(value);
      } catch (e) {
        console.warn('[swiper] invalid JSON in attribute:', raw, e);
        return value;
      }
    }
    // Numeric (but keep things like "auto" or "1/3" as strings).
    if (value !== '' && !isNaN(value)) return Number(value);
    return value;
  }

  /** camelCase the dataset key minus its `swiper` prefix: swiperSlidesPerView -> slidesPerView. */
  function optionName(datasetKey) {
    var rest = datasetKey.slice('swiper'.length);
    return rest.charAt(0).toLowerCase() + rest.slice(1);
  }

  /**
   * Finds the first matching descendant that belongs to *this* swiper and not a
   * nested one, so auto-wiring doesn't grab a child slider's controls.
   */
  function findOwn(container, selector) {
    var nodes = container.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].closest('.swiper') === container) return nodes[i];
    }
    return null;
  }

  /** Build the Swiper options object from an element's data-attributes. */
  function buildConfig(el) {
    var config = {};
    var key;

    // Start from defaults.
    for (key in DEFAULTS) config[key] = DEFAULTS[key];

    // 1) Full JSON blob (escape hatch for complex configs).
    if (el.dataset.swiperConfig) {
      try {
        var blob = JSON.parse(el.dataset.swiperConfig);
        for (key in blob) config[key] = blob[key];
      } catch (e) {
        console.warn('[swiper] invalid data-swiper-config JSON:', e);
      }
    }

    // 2) Individual data-swiper-* attributes (override the blob).
    for (key in el.dataset) {
      if (key.indexOf('swiper') !== 0 || RESERVED[key]) continue;
      config[optionName(key)] = coerce(el.dataset[key]);
    }

    // 3) Responsive breakpoints.
    if (el.dataset.swiperBreakpoints) {
      try {
        config.breakpoints = JSON.parse(el.dataset.swiperBreakpoints);
      } catch (e) {
        console.warn('[swiper] invalid data-swiper-breakpoints JSON:', e);
      }
    }

    // 4) Normalize autoplay shorthand: a number => delay (ms), true => default.
    if (typeof config.autoplay === 'number') {
      config.autoplay = { delay: config.autoplay };
    }
    if (config.autoplay && typeof config.autoplay === 'object') {
      config.autoplay = Object.assign(
        { disableOnInteraction: false, pauseOnMouseEnter: true },
        config.autoplay
      );
    }

    // 5) Auto-wire controls when their elements are present (unless opted out).
    if (config.pagination !== false) {
      var pgEl = findOwn(el, '.swiper-pagination');
      if (pgEl) {
        config.pagination = Object.assign(
          { el: pgEl, type: 'bullets', clickable: true },
          typeof config.pagination === 'object' ? config.pagination : {}
        );
      } else {
        delete config.pagination;
      }
    }

    if (config.navigation !== false) {
      var nextEl = findOwn(el, '.swiper-button-next');
      var prevEl = findOwn(el, '.swiper-button-prev');
      if (nextEl && prevEl) {
        config.navigation = Object.assign(
          { nextEl: nextEl, prevEl: prevEl },
          typeof config.navigation === 'object' ? config.navigation : {}
        );
      } else if (!(config.navigation && config.navigation.nextEl && config.navigation.prevEl)) {
        // Keep an explicit {nextEl, prevEl} config for controls outside the container.
        delete config.navigation;
      }
    }

    if (config.scrollbar !== false) {
      var sbEl = findOwn(el, '.swiper-scrollbar');
      if (sbEl) {
        config.scrollbar = Object.assign(
          { el: sbEl, draggable: true },
          typeof config.scrollbar === 'object' ? config.scrollbar : {}
        );
      } else {
        delete config.scrollbar;
      }
    }

    return config;
  }

  /** Construct the Swiper for `el` (idempotent). */
  function create(el) {
    if (el.classList.contains('swiper-initialized') || el.__swiperInit) return el.swiper;
    el.__swiperInit = true;
    var swiper = new Swiper(el, buildConfig(el));
    // Lets section scripts attach event handlers, which can't be set via data-attributes.
    el.dispatchEvent(new CustomEvent('swiper:init', { bubbles: true, detail: { swiper: swiper } }));
    return swiper;
  }

  /**
   * Initialize one element: either immediately, or lazily via
   * IntersectionObserver. Swiper construction always waits for the lib.
   */
  function init(el) {
    if (!el || el.__swiperInit || el.__swiperWaiting) return;

    if (el.dataset.swiperInitAfter === 'load' && document.readyState !== 'complete') {
      el.__swiperWaiting = true;
      window.addEventListener('load', function () {
        el.__swiperWaiting = false;
        init(el);
      }, { once: true });
      return;
    }

    var eager = el.dataset.swiperInitOnVisible === 'false';
    if (eager || !('IntersectionObserver' in window)) {
      whenReady(function () {
        create(el);
      });
      return;
    }

    var margin = el.dataset.swiperLazyMargin || '300px 0px';
    var io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            io.disconnect();
            whenReady(function () {
              create(el);
            });
            return;
          }
        }
      },
      { rootMargin: margin }
    );
    io.observe(el);
  }

  /** Initialize every uninitialized slider within `root` (defaults to document). */
  function initAll(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  // Public API.
  window.Branch = window.Branch || {};
  window.Branch.swiper = {
    init: init,
    initAll: initAll,
    create: create,
    buildConfig: buildConfig,
    get: function (el) {
      return el ? el.swiper : null;
    },
  };

  // Boot.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor support: (re)init / tear down on section changes.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var nodes = e.target.querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].swiper) nodes[i].swiper.destroy(true, true);
      nodes[i].__swiperInit = false;
    }
  });
})();
